import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import {
  AcademicAppointment,
  displayTime,
  sessionEnd,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import { SessionJoinService } from '../../../../core/services/academic-appointments/session-join.service';
import { ErrorStateComponent } from '../../error-state/error-state.component';

/** Cadence de rafraîchissement de l'horloge interne : la minute suffit. */
const TICK_MS = 60_000;

/** Les trois lectures d'un emploi du temps : maintenant, aujourd'hui, ensuite. */
export type SessionTab = 'today' | 'live' | 'upcoming';

/**
 * Carte « Programme » des tableaux de bord.
 *
 * <p>Purement présentationnelle : l'appelant fournit les séances déjà chargées
 * depuis son propre calendrier (prof ou élève), sur la quinzaine et non sur la
 * seule journée — les onglets en dépendent.</p>
 *
 * <p>Les onglets répartissaient auparavant les séances entre présentiel et en
 * ligne. C'était classer selon un détail : le professeur veut savoir ce qui se
 * passe <b>maintenant</b>, ce qui reste <b>aujourd'hui</b> et ce qui vient
 * <b>ensuite</b> — le mode d'une séance se lit d'un coup d'œil sur sa ligne, il
 * ne mérite pas de couper la liste en deux.</p>
 */
@Component({
  selector: 'app-today-classes',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterLink, ErrorStateComponent],
  templateUrl: './today-classes.component.html',
  styleUrl: './today-classes.component.css',
})
export class TodayClassesComponent {
  readonly view = inject(SessionViewService);
  private readonly joinService = inject(SessionJoinService);

  /**
   * Séances chargées par l'appelant, sur la quinzaine.
   *
   * <p>Les trois onglets s'en déduisent : les restreindre au jour même priverait
   * « prochaines » de tout contenu.</p>
   */
  @Input({ required: true }) set sessions(value: AcademicAppointment[]) {
    this.all.set(value ?? []);
  }

  @Input() loading = false;
  @Input() errored = false;

  /** Lien « voir tout » — diffère entre prof et élève. */
  @Input() calendarLink = '/student/agenda';

  private readonly all = signal<AcademicAppointment[]>([]);

  /**
   * Horloge interne. Sans elle, « en cours » serait figé à l'heure du rendu :
   * un tableau de bord laissé ouvert afficherait indéfiniment la séance de 8h.
   */
  private readonly now = signal(Date.now());

  readonly activeTab = signal<SessionTab>('today');

  constructor() {
    const timer = setInterval(() => this.now.set(Date.now()), TICK_MS);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  /**
   * Séances du jour — toutes, y compris terminées.
   *
   * <p>Une séance passée reste au programme de la journée : la retirer à son
   * heure de fin ferait disparaître sous les yeux du professeur ce qu'il vient
   * de faire, et laisserait un tableau vide en fin d'après-midi.</p>
   */
  readonly today = computed(() => {
    const day = new Date().toISOString().slice(0, 10);
    return this.sorted().filter((s) => (s.scheduledDate ?? '').slice(0, 10) === day);
  });

  /** Séances commencées et non terminées. Sous-ensemble du jour. */
  readonly live = computed(() => this.sorted().filter((s) => this.isLive(s)));

  /**
   * Séances à venir, tous jours confondus.
   *
   * <p>Y compris la suite de la journée : « prochaines » désigne ce qui arrive,
   * pas ce qui arrive un autre jour.</p>
   */
  readonly upcoming = computed(() => {
    const t = this.now();
    return this.sorted().filter((s) => new Date(sessionStart(s)).getTime() > t);
  });

  readonly visible = computed(() => {
    switch (this.activeTab()) {
      case 'live':     return this.live();
      case 'upcoming': return this.upcoming();
      default:         return this.today();
    }
  });

  /** Nombre de séances en cours — sert aussi à la pastille de l'entête. */
  readonly liveCount = computed(() => this.live().length);

  /**
   * Tri sur le début réel, et non sur la seule heure : les onglets couvrent
   * maintenant plusieurs jours, et comparer « 08:00 » à « 14:00 » sans la date
   * mélangerait demain matin avec cet après-midi.
   */
  private sorted(): AcademicAppointment[] {
    return [...this.all()].sort((a, b) =>
      sessionStart(a).localeCompare(sessionStart(b)),
    );
  }

  /** Une séance se tient en ligne dès qu'elle porte un lien de réunion. */
  isOnline(s: AcademicAppointment): boolean {
    return !!s.meetingUrl;
  }

  /** Séance commencée et pas encore terminée. */
  isLive(s: AcademicAppointment): boolean {
    const t = this.now();
    return new Date(sessionStart(s)).getTime() <= t
        && new Date(sessionEnd(s)).getTime() >= t;
  }

  /** Séance dont l'heure de fin est dépassée : conservée, mais estompée. */
  isDone(s: AcademicAppointment): boolean {
    return new Date(sessionEnd(s)).getTime() < this.now();
  }

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  /** La réunion s'ouvre au clic ; l'accès est journalisé côté serveur. */
  join(s: AcademicAppointment): void {
    this.joinService.openMeeting(s.id, (err) =>
      console.warn('Join session failed:', err),
      s.meetingUrl,
    );
  }
}
